/* ==========================================================================
   pdp-klarna-live.js
   Klarna and Humm on the LIVE default product template, matched to the
   product.default-v2 treatment.

   Three jobs:

     1. Pair them. v2's .product-info__wrapper is a flex-wrap row so its
        children can just take a 50% basis. Here that wrapper is display:block,
        so the two elements are moved into a .bnpl-pair flex container rather
        than changing the layout model for every block on the page.

        Only ever paired when BOTH have actually rendered content, tested
        structurally (Klarna's shadow .container exists / Humm's widget wrote
        text or an image) rather than by height, so an app that fails to paint
        leaves the other one full width instead of sitting beside an empty
        bordered box. Unwraps again if one later disappears.

     2. Restyle Klarna's box from inside its shadow root - fill the host so it
        matches Humm's height, and stack the badge above the sentence so the
        copy gets the full width of a half-width column. Ungated, matching v2.

     3. Make the whole Klarna box open Klarna, matching Humm, whose widget
        wraps its box in an anchor.

   Klarna's shadow root is OPEN, which is what makes 2 and 3 possible. If
   Klarna ever closes it both fail safe: the box keeps its native layout and
   Klarna's own "Learn more" still works.
   ========================================================================== */

(function () {
  'use strict';

  var STYLE_ID = 'pdp-klarna-live';

  var CSS =
    ':host{display:block!important;height:100%!important}' +
    ':host>div,.loaded{display:block!important;height:100%!important}' +
    '.container{height:100%!important;box-sizing:border-box!important;' +
    'flex-direction:column!important;align-items:center!important;' +
    'gap:4px!important;padding:8px!important}' +
    '.text-wrapper{width:100%!important}';

  function wrapper() {
    return document.querySelector('.product-info__wrapper');
  }

  function placement() {
    var w = wrapper();
    return w ? w.querySelector('klarna-placement') : null;
  }

  function klarnaBlock() {
    var el = placement();
    return el ? el.closest('.product-info__block') : null;
  }

  function hummBox() {
    var w = wrapper();
    return w ? w.querySelector('.humm-wrapper') : null;
  }

  /* Structural content tests, not height - height is 0 for a moment during
     layout and would make the pair flicker in and out. */
  function klarnaHasContent() {
    var el = placement();
    return !!(el && el.shadowRoot && el.shadowRoot.querySelector('.container'));
  }

  function hummHasContent() {
    var box = hummBox();
    if (!box) return false;
    return (box.textContent || '').trim() !== '' || !!box.querySelector('img');
  }

  /* ----------------------------------------------------------------- pair */

  function syncPair() {
    var w = wrapper();
    if (!w) return;

    var kb = klarnaBlock();
    var hb = hummBox();
    var pair = w.querySelector('.bnpl-pair');
    var both = kb && hb && klarnaHasContent() && hummHasContent();

    if (both) {
      if (pair && kb.parentElement === pair && hb.parentElement === pair) return;
      if (!pair) {
        pair = document.createElement('div');
        pair.className = 'bnpl-pair';
        w.insertBefore(pair, kb);
      }
      pair.appendChild(kb);
      pair.appendChild(hb);
      return;
    }

    /* One of them is not there - put whatever is back, full width. */
    if (pair) {
      while (pair.firstChild) w.insertBefore(pair.firstChild, pair);
      pair.parentNode.removeChild(pair);
    }
  }

  /* -------------------------------------------------------------- styling */

  function style() {
    var el = placement();
    if (!el || !el.shadowRoot) return false;

    var root = el.shadowRoot;
    if (root.getElementById(STYLE_ID)) return true;
    if (!root.querySelector('.container')) return false;

    try {
      var tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.textContent = CSS;
      root.appendChild(tag);
      return true;
    } catch (e) {
      if (window.console && console.warn) console.warn('[pdp-klarna-live]', e);
      return false;
    }
  }

  /* ---------------------------------------------------------------- click */

  /* CAPTURE phase is essential. Klarna calls stopPropagation on clicks inside
     its container, so a bubble listener never fires on a real click.
     composedPath sees into the shadow tree, so a genuine click on Klarna's own
     control is left alone and never double-fires. */
  function bindClick() {
    var block = klarnaBlock();
    if (!block || block.getAttribute('data-pdp-klarna-click')) return;

    block.setAttribute('data-pdp-klarna-click', '1');
    block.style.cursor = 'pointer';

    block.addEventListener('click', function (event) {
      var el = placement();
      var root = el ? el.shadowRoot : null;
      if (!root) return;

      var trigger = root.querySelector('button') || root.querySelector('a');
      if (!trigger) return;

      var path = event.composedPath ? event.composedPath() : [];
      if (path.indexOf(trigger) > -1) return;

      event.stopPropagation();
      trigger.click();
    }, true);
  }

  /* ------------------------------------------------------------------ run */

  function pass() {
    style();
    bindClick();
    syncPair();
  }

  function boot() {
    pass();

    /* Klarna and Humm both paint late and Klarna re-renders on variant change,
       which can wipe the injected style, so poll briefly then watch. */
    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      pass();
      if (tries > 40) window.clearInterval(timer);
    }, 250);

    if (!('MutationObserver' in window)) return;
    var w = wrapper();
    if (!w) return;

    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        observer.disconnect();
        pass();
        observer.observe(w, { childList: true, subtree: true });
      }, 250);
    });
    observer.observe(w, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();